/* @flow */
import cx from 'classnames';
import * as React from 'react';
import PropTypes from 'prop-types';

import ChatActions from 'chat/actions/ChatActions';
import DeprecatedTokenizerTypeahead from 'components/DeprecatedTokenizerTypeahead';
import getMatcher from 'utils/getMatcher';
import LoadingIndicator from 'components/LoadingIndicator';
import MessageStarterItem from './MessageStarterItem';
import PotentialChatMember from 'entities/PotentialChatMember';
import Text from 'components/Text';
import Glyph from 'components/Glyph';
import PhoneCallDenialReasonTooltip from 'phoneCalls/components/PhoneCallDenialReasonTooltip';
import { isFixableDenialReason } from 'phoneCalls/components/PhoneCallModal';

import './styles/IndividualMessageStarter.scss';

function StatusIndicator({
  member,
  showPhoneCallStatus,
  highlighted,
}: {
  member: PotentialChatMember,
  showPhoneCallStatus: boolean,
  highlighted: boolean,
}) {
  const u13 = member.isUnder13();
  if (u13) {
    return (
      <Text smallCaps color="percussion" size="xsmall">
        UNDER 13
      </Text>
    );
  } else if (showPhoneCallStatus) {
    const user = member.getUser();
    const canCall = user.getCanPhone();
    const color = canCall ? 'mascot' : 'percussion';
    const name = highlighted && canCall ? 'phone-filled' : 'phone';
    return (
      <PhoneCallDenialReasonTooltip
        denialReason={user.getCanPhoneDenialReason()}
        calleeName={user.getName()}>
        <Glyph color={color} name={name} size="medium" />
      </PhoneCallDenialReasonTooltip>
    );
  } else {
    return null;
  }
}

type Props = {
  onRequestHide?: Function,
  onSelect: Function,
  className?: string,
  placeholderText?: string,
  emptyStateElement?: *,
  chatMembersFilter?: ?Function,
  showPhoneCallStatus?: boolean,
};

type State = {
  inputValue: ?string,
};

class IndividualMessageStarter extends React.Component<Props, State> {
  state: State = {
    inputValue: '',
  };

  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  handleInputChange = (value: string) => {
    this.setState({ inputValue: value });
  };

  shouldShowEmptyState = () => {
    return !this.state.inputValue && this.props.emptyStateElement;
  };

  getFilter = (query: string) => {
    const matcher = getMatcher(query);
    return (member: PotentialChatMember) => matcher([member.getQueryKey()]);
  };

  fetchData = (query: string) => {
    return ChatActions.promisePotentialChatMembers(query).then(collection => {
      const filteredCollection = this.props.chatMembersFilter
        ? collection.filterToCollection(this.props.chatMembersFilter)
        : collection;
      return filteredCollection.filterToCollection(
        member => !member.isUnavailable || member.isUnder13
      );
    });
  };

  handleSelect = (members: PotentialChatMember[]) => {
    const member = members[0];
    if (this.props.onSelect) {
      this.props.onSelect(member);
    }
  };

  renderInputToken = () => {
    // N/A because we're short-cutting out of tokenizing
    return <b>error</b>;
  };

  renderList = (items: Array<*>, loading: boolean) => {
    if (this.shouldShowEmptyState()) {
      return this.props.emptyStateElement;
    }
    return (
      <div className="IndividualMessageStarter-resultsWrap">
        {loading ? (
          <div className="IndividualMessageStarter-loadingIndicator">
            <LoadingIndicator />
          </div>
        ) : (
          <ul className="IndividualMessageStarter-results">{items}</ul>
        )}
      </div>
    );
  };

  renderNoResults = (query: string) => {
    return (
      <li key="noResults">
        <Text color="chalkboard" size="xsmall" weight="semibold">
          {`No people found for "${query}"`}
        </Text>
      </li>
    );
  };

  renderMember = (
    member: PotentialChatMember,
    highlighted: boolean,
    query: string,
    onSelect: Function
  ) => {
    if (this.shouldShowEmptyState()) {
      return <div />;
    }
    const { showPhoneCallStatus } = this.props;
    const user = member.getUser();
    const couldPhone =
      !showPhoneCallStatus ||
      user.getCanPhone() ||
      isFixableDenialReason(user.getCanPhoneDenialReason());
    const disabled = member.isUnder13() || !couldPhone;
    return (
      <li onClick={() => !disabled && onSelect()} key={member.id}>
        <MessageStarterItem
          className={cx('IndividualMessageStarter-results-item', {
            'IndividualMessageStarter-results-item--highlight': !disabled && highlighted,
            'IndividualMessageStarter-results-item--disabled': disabled,
          })}
          member={member}
          query={query}
          selectable={false}
          selected={false}
          statusIndicator={
            <StatusIndicator
              member={member}
              showPhoneCallStatus={!!showPhoneCallStatus}
              highlighted={highlighted}
            />
          }
        />
      </li>
    );
  };

  render() {
    return (
      <div className={cx('IndividualMessageStarter', this.props.className)}>
        <DeprecatedTokenizerTypeahead
          prompt="To:"
          placeholder={this.props.placeholderText || 'Choose or search for an individual'}
          onChange={this.handleSelect}
          dataSource={this.fetchData}
          dataFilterFactory={this.getFilter}
          listRenderer={this.renderList}
          noResultsRenderer={this.renderNoResults}
          itemRenderer={this.renderMember}
          tokenRenderer={this.renderInputToken}
          onInputChange={this.handleInputChange}
          autoFocus
          inline
        />
      </div>
    );
  }
}

export { IndividualMessageStarter };

export default IndividualMessageStarter;
